import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Link2, Copy, Trash2, Eye, EyeOff, Share2 } from 'lucide-react';
import { getApiUrl } from '../utils/config';

const API = getApiUrl();

const ShareLinkModal = ({ car, isOpen, onClose, userPlan }) => {
  const [shareLink, setShareLink] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({
    show_make_model: true,
    show_year: true,
    show_mileage: true,
    show_photos: true,
    show_service_history: true,
    show_fuel_economy: true,
    show_parts_list: true
  });

  // Check if user has public share link access (Basic, Premium, or Professional)
  const hasShareLinkAccess = userPlan === 'Basic' || userPlan === 'Premium' || userPlan === 'Professional';

  useEffect(() => {
    if (isOpen && car) {
      fetchShareLink();
    }
  }, [isOpen, car]);

  const fetchShareLink = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/cars/${car.id}/public-share`);
      setShareLink(response.data);
      setPrivacySettings(response.data.privacy_settings);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching share link:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generateOrUpdateLink = async () => {
    if (isSubmitting) return;
    
    // Check if user has access before proceeding
    if (!hasShareLinkAccess) {
      toast.error('Public Share Links are available on Basic plan and above. Upgrade to Basic or Premium to create shareable links.', {
        duration: 5000,
        icon: '🔒',
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/cars/${car.id}/public-share`, {
        privacy_settings: privacySettings
      });
      setShareLink(response.data);
      toast.success(shareLink ? 'Share link updated!' : 'Share link created!');
    } catch (error) {
      console.error('Error creating/updating share link:', error);
      // Check if it's a feature restriction error
      if (error.response?.status === 403 && error.response?.data?.detail?.error === 'feature_restricted') {
        const errorDetail = error.response.data.detail;
        toast.error(errorDetail.message || 'Public Share Links are available on Basic plan and above. Upgrade to access this feature.', {
          duration: 5000,
        });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create share link');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    const url = `${window.location.origin}/public-car/${shareLink.share_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  const deleteLink = async () => {
    if (isSubmitting) return;
    
    if (!confirm('Are you sure you want to disable this share link? The link will no longer work.')) {
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.delete(`${API}/cars/${car.id}/public-share`);
      setShareLink(null);
      toast.success('Share link disabled');
    } catch (error) {
      console.error('Error deleting share link:', error);
      toast.error('Failed to disable share link');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSetting = (key) => {
    setPrivacySettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const privacyOptions = [
    { key: 'show_make_model', label: 'Make & Model', icon: <Eye className="w-4 h-4" /> },
    { key: 'show_year', label: 'Year', icon: <Eye className="w-4 h-4" /> },
    { key: 'show_mileage', label: 'Mileage/Odometer', icon: <Eye className="w-4 h-4" /> },
    { key: 'show_photos', label: 'Photos', icon: <Eye className="w-4 h-4" /> },
    { key: 'show_service_history', label: 'Service History', icon: <Eye className="w-4 h-4" /> },
    { key: 'show_fuel_economy', label: 'Fuel Economy', icon: <Eye className="w-4 h-4" /> },
    { key: 'show_parts_list', label: 'Parts List', icon: <Eye className="w-4 h-4" /> }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-600" />
            Public Share Link
          </DialogTitle>
          <DialogDescription>
            Create a read-only link to share your car information publicly (perfect for social media posts!)
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="space-y-6">
            {/* Upgrade Banner for Free Users */}
            {!hasShareLinkAccess && (
              <div className="p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-xl">🔒</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-orange-900 mb-1">Upgrade to Basic or Premium Required</h4>
                    <p className="text-sm text-orange-700">
                      Public Share Links are available on Basic plan and above. Upgrade your subscription to create shareable links for your vehicles.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Privacy Settings */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">What to Show</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {privacyOptions.map(option => (
                  <div key={option.key} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-2">
                      {privacySettings[option.key] ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                      <span className="text-sm">{option.label}</span>
                    </div>
                    <Switch
                      checked={privacySettings[option.key]}
                      onCheckedChange={() => toggleSetting(option.key)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Share Link Display */}
            {shareLink && shareLink.is_active && (
              <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Label className="text-sm font-medium">Your Public Link</Label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/public-car/${shareLink.share_token}`}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border rounded-lg text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyToClipboard}
                    className="flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-gray-600">
                  Share this link on social media, forums, or anywhere you want to showcase your car!
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              {shareLink && shareLink.is_active ? (
                <>
                  <Button
                    onClick={generateOrUpdateLink}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Updating...</span>
                      </div>
                    ) : (
                      'Update Settings'
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={deleteLink}
                    disabled={isSubmitting}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Disable Link
                  </Button>
                </>
              ) : (
                <Button
                  onClick={generateOrUpdateLink}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Creating...</span>
                    </div>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4 mr-2" />
                      Generate Share Link
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareLinkModal;
